/**
 * Enhanced Voice Activity Detection for accurate speech detection and interruption validation
 * @class
 */
export class EnhancedVAD {
  constructor() {
    // Constants for voice detection
    this.bufferSize = 2048;
    this.minNoiseLevel = -50; // dB
    this.speechThreshold = -40; // dB
    this.minSpeechDuration = 250; // ms
    this.maxSilenceDuration = 500; // ms
    this.spectralFlatnessTreshold = 0.5;
    this.zcrThreshold = 0.25;
    this.maxNoiseFrames = 3;
    this.sustainedSpeechThreshold = 4;
    this.energyVarianceThreshold = 0.15;

    // State variables
    this.lastSpeechTimestamp = 0;
    this.isSpeaking = false;
    this.consecutiveNoiseFrames = 0;
    this.speechStartTime = null;
    this.consecutiveSpeechFrames = 0;
    this.recentEnergies = [];
    this.maxRecentEnergies = 10;
  }

  calculateSpectralFlatness(buffer) {
    const geometricMean = Math.exp(
      buffer.reduce((sum, val) => sum + Math.log(Math.abs(val) + 1e-10), 0) /
        buffer.length,
    );
    const arithmeticMean =
      buffer.reduce((sum, val) => sum + Math.abs(val), 0) / buffer.length;
    return geometricMean / arithmeticMean;
  }

  calculateZCR(buffer) {
    let crossings = 0;
    for (let i = 1; i < buffer.length; i++) {
      if (
        (buffer[i - 1] >= 0 && buffer[i] < 0) ||
        (buffer[i - 1] < 0 && buffer[i] >= 0)
      ) {
        crossings++;
      }
    }
    return crossings / buffer.length;
  }

  calculateDB(buffer) {
    const rms = Math.sqrt(
      buffer.reduce((sum, val) => sum + val * val, 0) / buffer.length,
    );
    const db = 20 * Math.log10(rms);

    this.recentEnergies.push(db);
    if (this.recentEnergies.length > this.maxRecentEnergies) {
      this.recentEnergies.shift();
    }

    return db;
  }

  calculateEnergyVariance() {
    if (this.recentEnergies.length < 2) return 0;

    const mean =
      this.recentEnergies.reduce((a, b) => a + b) / this.recentEnergies.length;
    const variance =
      this.recentEnergies.reduce((a, b) => a + Math.pow(b - mean, 2), 0) /
      this.recentEnergies.length;
    return variance;
  }

  /**
   * Updates speech state and timestamps with fixed speech time tracking
   */
  updateSpeechState(isSpeechLike, currentTime) {
    if (isSpeechLike) {
      // Reset noise counter and increment speech counter
      this.consecutiveNoiseFrames = 0;
      this.consecutiveSpeechFrames++;

      // Set initial speech time if we've met the threshold for sustained speech
      if (this.consecutiveSpeechFrames >= this.sustainedSpeechThreshold) {
        if (!this.speechStartTime) {
          this.speechStartTime = currentTime;
        }
        if (!this.isSpeaking) {
          this.isSpeaking = true;
        }
        this.lastSpeechTimestamp = currentTime;
      }
    } else {
      this.consecutiveNoiseFrames++;

      // Only reset speech tracking if we've had enough noise frames
      if (this.consecutiveNoiseFrames >= this.maxNoiseFrames) {
        this.consecutiveSpeechFrames = 0;

        // Only stop speech tracking if we've exceeded silence duration
        if (currentTime - this.lastSpeechTimestamp > this.maxSilenceDuration) {
          this.isSpeaking = false;
          this.speechStartTime = null;
        }
      }
    }
  }

  processAudioData(buffer) {
    const currentTime = Date.now();
    const db = this.calculateDB(buffer);

    if (db < this.minNoiseLevel) {
      return this.isSpeaking;
    }

    const spectralFlatness = this.calculateSpectralFlatness(buffer);
    const zcr = this.calculateZCR(buffer);
    const energyVariance = this.calculateEnergyVariance();

    const isSpeechLike =
      db >= this.speechThreshold &&
      spectralFlatness < this.spectralFlatnessTreshold &&
      zcr > this.zcrThreshold &&
      energyVariance < this.energyVarianceThreshold;

    this.updateSpeechState(isSpeechLike, currentTime);

    return this.isSpeaking;
  }

  /**
   * Checks if current audio represents a valid interruption
   * Now with proper speech time tracking
   */
  isValidInterruption(buffer) {
    // First process the audio to update speech state
    this.processAudioData(buffer);

    const currentTime = Date.now();

    // Debug log to help track speech detection
    console.log('Speech detection state:', {
      isSpeaking: this.isSpeaking,
      speechStartTime: this.speechStartTime,
      consecutiveSpeechFrames: this.consecutiveSpeechFrames,
      energyVariance: this.calculateEnergyVariance(),
      speechDuration: this.speechStartTime
        ? currentTime - this.speechStartTime
        : 0,
    });

    // Verify we have active speech
    if (!this.isSpeaking || !this.speechStartTime) {
      return false;
    }

    // Check duration and quality of speech
    const speechDuration = currentTime - this.speechStartTime;
    const hasMinDuration = speechDuration >= this.minSpeechDuration;
    const hasConsistentEnergy =
      this.calculateEnergyVariance() < this.energyVarianceThreshold;
    const hasSustainedSpeech =
      this.consecutiveSpeechFrames >= this.sustainedSpeechThreshold;

    return hasMinDuration && hasConsistentEnergy && hasSustainedSpeech;
  }

  reset() {
    this.lastSpeechTimestamp = 0;
    this.isSpeaking = false;
    this.consecutiveNoiseFrames = 0;
    this.speechStartTime = null;
    this.consecutiveSpeechFrames = 0;
    this.recentEnergies = [];
  }
}

// Make available globally like other analysis modules
globalThis.EnhancedVAD = EnhancedVAD;
